import { openDB } from 'idb';

const DB_NAME = process.env.DB_NAME || 'therapix';
const STORE_NAME = process.env.STORE_NAME || 'audioNotes';

export interface AudioFileData {
	patientId?: string;
	Soapid?: string;
	SoapType?: string;
	audio: Blob | undefined;
	oldTime?: number;
	recordingTime?: number;
	patientName?: string;
    status?: string;
}

export interface AudioFileDataWithoutBlob {
	patientId?: string;
	Soapid?: string;
	SoapType?: string;
	oldTime?: number;
	recordingTime?: number;
	patientName?: string;
    status?: string;
}

export const initDB = async () => {
    //test
    const db= await openDB(DB_NAME, 1, {
        upgrade(db) {
            if(!db.objectStoreNames.contains(STORE_NAME)) {
                const store = db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
                store.createIndex('patientId_Soapid', ['patientId', 'Soapid'], { unique: true });
            }
        }
    });
    return db;
};

export const addData = async (data: AudioFileData) => {
    const db = await initDB();
    return db.add(STORE_NAME, data);
};

export const getAllData = async () => {
    const db = await initDB();
    return await db.getAll(STORE_NAME);
};

export const getAudioByBothId = async (patientId: string, Soapid: string): Promise<AudioFileData> => {
    const db = await openDB(DB_NAME, 1);
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const index = store.index('patientId_Soapid');

    const data = await index.get([patientId, Soapid]);

    return data;
};

export const getAudioData = async (audio: AudioFileDataWithoutBlob): Promise<AudioFileDataWithoutBlob> => {
    if (!audio.patientId || !audio.Soapid) return audio;
    const data = await getAudioByBothId(audio.patientId, audio.Soapid);
    return data;
};
export const getAudioBlob  = async (audio: AudioFileDataWithoutBlob): Promise<Blob | undefined> => {
    if (!audio.patientId || !audio.Soapid) return;
    const data = await getAudioByBothId(audio.patientId, audio.Soapid);
    return data?.audio;
}

export const addUpdateData = async (data: AudioFileData) => {
    if (!data.patientId || !data.Soapid) return;
    const db = await initDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);

    let existingRecord;
    const index = store.index('patientId_Soapid');
    existingRecord = await index.get([data.patientId, data.Soapid]);
    if (existingRecord) {
        const updatedRecord = { ...existingRecord, ...data };
        await store.put(updatedRecord);
    } else {
        await store.add(data);
    }
    await tx.done;
};

export const removeData = async (audio: AudioFileDataWithoutBlob) => {
    if (!audio.patientId || !audio.Soapid) return;
    const db = await initDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    let existingRecord;
    try {
        const index = store.index('patientId_Soapid');
        existingRecord = await index.get([audio.patientId, audio.Soapid]);
        if (existingRecord) {
            await store.delete(existingRecord.id);
        } else {
            console.log(`No record found for patientId ${audio.patientId} and Soapid ${audio.Soapid}.`);
        }
        await tx.done;
    } catch (error) {
        console.error("Error deleting record:", error);
    }
};
export const removeDataSingle = async (pid: string) => {
    if (!pid) return;
    const db = await initDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);

    try {
        const allRecords = await store.getAll(); // Get all records
        const recordsToDelete = allRecords.filter(record => record.patientId === pid);

        if (recordsToDelete.length > 0) {
            for (const record of recordsToDelete) {
                await store.delete(record.id); // Delete each matching record
            }
        } else {
            console.log(`No records found for patientId ${pid}.`);
        }

        await tx.done;
    } catch (error) {
        console.error("Error deleting records:", error);
    }
};
